'use client';
import Image from 'next/image';
import React, { useState, useEffect } from 'react';

interface SliderProps {
  children: React.ReactNode[];
  className?: string;
  showControls?: boolean;
  showIndicators?: boolean;
  autoPlay?: boolean;
  autoPlayInterval?: number;
  buttonClassName?: string;
}

const Slider: React.FC<SliderProps> = ({
  children,
  className = '',
  showControls = true,
  showIndicators = true,
  autoPlay = false,
  autoPlayInterval = 3000,
  buttonClassName = '',
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => (prevIndex === 0 ? children.length - 1 : prevIndex - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex === children.length - 1 ? 0 : prevIndex + 1));
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  React.useEffect(() => {
    if (autoPlay) {
      const interval = setInterval(goToNext, autoPlayInterval);
      return () => clearInterval(interval);
    }
  }, [autoPlay, autoPlayInterval, currentIndex]);

  return (
    <div data-component-id="src/components/common/Slider.tsx:46:4" data-component-path="src/components/common/Slider.tsx" data-component-line="46" data-component-end-line="113" data-component-file="Slider.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`relative w-full overflow-hidden ${className}`}>
      {/* Slider Container */}
      <div data-component-id="src/components/common/Slider.tsx:48:6" data-component-path="src/components/common/Slider.tsx" data-component-line="48" data-component-end-line="59" data-component-file="Slider.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20w-full%20h-%5B150px%5D%20md%3Ah-%5B250px%5D%20lg%3Ah-%5B350px%5D%20xl%3Ah-%5B550px%5D%22%7D" className="relative w-full h-[150px] md:h-[250px] lg:h-[350px] xl:h-[550px]">
        <div data-component-id="src/components/common/Slider.tsx:49:8" data-component-path="src/components/common/Slider.tsx" data-component-line="49" data-component-end-line="58" data-component-file="Slider.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20transition-transform%20duration-300%20ease-in-out%20h-full%22%7D"
          className="flex transition-transform duration-300 ease-in-out h-full"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {children.map((child, index) => (
            <div data-component-id="src/components/common/Slider.tsx:54:12" data-component-path="src/components/common/Slider.tsx" data-component-line="54" data-component-end-line="56" data-component-file="Slider.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%20flex-shrink-0%20h-full%22%7D" key={index} className="w-full flex-shrink-0 h-full">
              {child}
            </div>
          ))}
        </div>
      </div>

      {/* Navigation Controls */}
      {showControls && (
        <>
          <button data-component-id="src/components/common/Slider.tsx:64:10" data-component-path="src/components/common/Slider.tsx" data-component-line="64" data-component-end-line="78" data-component-file="Slider.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
            onClick={goToPrevious}
            className={`absolute left-4 sm:left-6 md:left-8 lg:left-[67px] top-1/2 transform -translate-y-1/2 w-[48px] h-[48px] bg-slider-3 rounded-[24px] shadow-[0px_4px_6px_#00000019] flex items-center justify-center hover:bg-opacity-90 transition-all duration-200 z-10 ${buttonClassName}`}
            aria-label="Previous slide"
          >
            <img data-component-id="src/components/common/Slider.tsx:69:12" data-component-path="src/components/common/Slider.tsx" data-component-line="69" data-component-end-line="77" data-component-file="Slider.tsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22src%22%3A%22%2Fimages%2Fimg_arrow_left.svg%22%2C%22alt%22%3A%22Previous%22%2C%22className%22%3A%22w-6%20h-6%22%7D"
              src="/images/img_arrow_left.svg"
              alt="Previous"
              className="w-6 h-6"
              width={24}
              height={24}
              priority={false}
              loading="lazy"
            />
          </button>

          <button data-component-id="src/components/common/Slider.tsx:80:10" data-component-path="src/components/common/Slider.tsx" data-component-line="80" data-component-end-line="94" data-component-file="Slider.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
            onClick={goToNext}
            className={`absolute right-4 sm:right-6 md:right-8 lg:right-[67px] top-1/2 transform -translate-y-1/2 w-[48px] h-[48px] bg-slider-3 rounded-[24px] shadow-[0px_4px_6px_#00000019] flex items-center justify-center hover:bg-opacity-90 transition-all duration-200 z-10 ${buttonClassName}`}
            aria-label="Next slide"
          >
            <img data-component-id="src/components/common/Slider.tsx:85:12" data-component-path="src/components/common/Slider.tsx" data-component-line="85" data-component-end-line="93" data-component-file="Slider.tsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22src%22%3A%22%2Fimages%2Fimg_arrow_right_blue_gray_800_02.svg%22%2C%22alt%22%3A%22Next%22%2C%22className%22%3A%22w-6%20h-6%22%7D"
              src="/images/img_arrow_right_blue_gray_800_02.svg"
              alt="Next"
              className="w-6 h-6"
              width={24}
              height={24}
              priority={false}
              loading="lazy"
            />
          </button>
        </>
      )}

      {/* Indicators */}
      {showIndicators && (
        <div data-component-id="src/components/common/Slider.tsx:100:8" data-component-path="src/components/common/Slider.tsx" data-component-line="100" data-component-end-line="111" data-component-file="Slider.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20bottom-4%20sm%3Abottom-6%20md%3Abottom-8%20left-1%2F2%20transform%20-translate-x-1%2F2%20flex%20gap-%5B16px%5D%20z-10%22%7D" className="absolute bottom-4 sm:bottom-6 md:bottom-8 left-1/2 transform -translate-x-1/2 flex gap-[16px] z-10">
          {children.map((_, index) => (
            <button data-component-id="src/components/common/Slider.tsx:102:12" data-component-path="src/components/common/Slider.tsx" data-component-line="102" data-component-end-line="109" data-component-file="Slider.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-[8px] h-[8px] rounded-[4px] transition-all duration-200 ${
                index === currentIndex ? 'bg-global-2' : 'bg-global-1'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Slider;
